import { LocationCard, LocationPicker } from '@components/location-picker';
import { useCallback, useState } from 'react';
import Slider from 'react-slick';
import AddIcon from '@material-ui/icons/Add';
import SkipNextIcon from '@material-ui/icons/SkipNext';
import SkipPreviousIcon from '@material-ui/icons/SkipPrevious';
import styled from 'styled-components';
import { lightBlue } from '@material-ui/core/colors';
import { GenericLocationItem } from '@api/map/types';
import { useEffect } from 'react';
import { useBoolean, useSnackbarInstance } from '@lib/common';
import { createFormField } from '../common';

interface OtherOfficesProps {
  value: GenericLocationItem[];
  onChange: (item: GenericLocationItem[]) => any;
}

export const OtherOffices = ({ value = null, onChange = null }: OtherOfficesProps) => {
  const [offices, setOffices] = useState<GenericLocationItem[]>(value || []);

  const [showLocationPicker, openLocationPicker, closeLocationPicker] = useBoolean(false);

  const updateOffices = useCallback(
    async (updater: (prev: GenericLocationItem[]) => GenericLocationItem[]) => {
      const nextVal = await new Promise<GenericLocationItem[]>(r => {
        setOffices(v => {
          const toUdp = updater(v);
          setImmediate(r, toUdp);
          return toUdp;
        });
      });
      onChange && onChange(nextVal);
    },
    [onChange, setOffices]
  );

  const { enqueue: snackbar } = useSnackbarInstance();

  const handleLocationPickerFinished = useCallback(
    (item: GenericLocationItem) => {
      const exists = offices.findIndex(
        ofc => ofc.city == item.city && ofc.state == item.state && ofc.country == item.country
      );
      if (exists >= 0) {
        snackbar(`The location you are trying to add already exists. (#${exists + 1})`, {
          variant: 'error',
          autoHideDuration: 4000,
        });
      } else {
        updateOffices(o => [item, ...o]);
      }
    },
    [updateOffices, offices]
  );

  const handleChange = (i: number) => value => {
    const nextOffices = [...offices];
    nextOffices[i] = value;
    updateOffices(() => nextOffices);
  };

  const handleDelete = (i: number) => () => {
    updateOffices(o => {
      const nextVal = [...o];
      nextVal.splice(i, 1);
      return nextVal;
    });
  };

  useEffect(() => {
    if (value !== offices) setOffices(value || []);
  }, [value]);

  const addAtEnd = offices.length > 3;

  return (
    <>
      <SliderContainer style={{ padding: 10, background: '#f5f5f5' }}>
        <Slider
          infinite={false}
          speed={500}
          slidesToShow={4}
          slidesToScroll={2}
          nextArrow={<NextArrow />}
          prevArrow={<PrevArrow />}>
          <div>
            <div style={{ paddingRight: 10, height: '100%' }}>
              <AddButton onClick={openLocationPicker} />
            </div>
          </div>
          {offices.map((office, i) => (
            <div key={i}>
              <div style={{ paddingRight: 10 }}>
                <LocationCard
                  title={`Other office #${i + 1}`}
                  value={office}
                  onChange={handleChange(i)}
                  onDelete={handleDelete(i)}
                />
              </div>
            </div>
          ))}
          {addAtEnd && (
            <div>
              <div style={{ paddingRight: 10, height: '100%' }}>
                <AddButton onClick={openLocationPicker} />
              </div>
            </div>
          )}
        </Slider>
      </SliderContainer>
      {showLocationPicker && (
        <LocationPicker
          fullWidth
          maxWidth="lg"
          open
          cardTitle="Other office"
          onClose={closeLocationPicker}
          onFinish={handleLocationPickerFinished}
        />
      )}
    </>
  );
};

export const OtherOfficesField = createFormField(OtherOffices);

const SliderContainer = styled.div`
  padding: 10px;
  height: '100%';
  .slick-track {
    display: flex;
    align-items: center;
  }
  *:focus {
    outline: none;
  }
`;

const NextArrow = ({ style, currentSlide, slideCount, ...props }: any) => {
  return (
    <SkipNextIcon
      style={{
        ...style,
        background: lightBlue[600],
        borderRadius: '50%',
        color: 'white',
        zIndex: 20,
      }}
      {...props}
    />
  );
};
const PrevArrow = ({ style, currentSlide, slideCount, ...props }: any) => {
  return (
    <SkipPreviousIcon
      style={{
        ...style,
        background: lightBlue[600],
        borderRadius: '50%',
        color: 'white',
        zIndex: 20,
      }}
      {...props}
    />
  );
};
const AddButton = styled(props => (
  <div {...props}>
    <div>
      <span>
        <AddIcon fontSize="large" />
        <br />
        Add New Location
      </span>
    </div>
  </div>
))`
  height: 100%;
  padding: 20px;
  cursor: pointer;
  & div {
    border: 5px dashed ${lightBlue[600]};
    display: flex;
    justify-content: center;
    align-items: center;
    height: 150px;
  }
  span {
    color: ${lightBlue[600]};
    text-align: center;
  }
`;
